import { createContext, ReactNode, useCallback, useContext, useState } from 'react'

// Define the allowed keys for the dialog registry
export enum Dialogs {
    SIGNIN = 'signIn',
    SIGNUP = 'signUp',
    SIGN_UP_SOCIAL = 'signUpSocial',
    FORGOT_PASSWORD = 'forgotPassword',
    RESET_PASSWORD = 'resetPassword',
    JOIN_US = 'joinUs',
    DIVE_IN = 'diveIn',
    ERROR = 'error',
    SETTINGS = 'settings',
    MISSIONS_APP_STORE = 'missionsAppStore',
    DAILY_BONUS = 'dailyBonus',
    FINISH_PUZZLES_FTUE = 'finishPuzzlesFtue',
    POPUP_SERVICE = 'popupService',
    FULLSCREEN_ANIMATION = 'fullscreenAnimation',
    HINT_LIMIT = 'hintLimit',
    DAILY_PUZZLE_FEEDBACK = 'dailyPuzzleFeedback',
    GAME_OVER = 'gameOver',
    PUZZLE_QUEST_RESULT = 'puzzleQuestResult',
    FTUE = 'ftue',
    PUZZLE_LIMIT = 'puzzleLimit',
    PUZZLE_TAGS = 'puzzleTags',
    MISSION_COMPLETE = 'missionComplete',
}
type DialogKeys = Dialogs

const FORCED_BY_DEFAULT_DIALOGS: DialogKeys[] = [
    Dialogs.SIGNIN,
    Dialogs.SIGNUP,
    Dialogs.SIGN_UP_SOCIAL,
    Dialogs.FORGOT_PASSWORD,
    Dialogs.RESET_PASSWORD,
]

// Define the type for the dialog registry
type DialogRegistryEntry = {
    dialog: ReactNode
    timestamp: number
}

type DialogRegistry = {
    [key in DialogKeys]?: DialogRegistryEntry
}

// Define the context type
interface DialogsContextType {
    pauseDialogs: boolean
    setPauseDialogs: (pause: boolean) => void
    dialogs: Array<ReactNode>
    dialogRegistry: DialogRegistry
    registerDialog: (key: DialogKeys, dialog: ReactNode, force?: boolean) => void
    unregisterDialog: (key: DialogKeys, callback?: Function) => void
}

// Create the context
const DialogsContext = createContext<DialogsContextType | undefined>(undefined)

// Provider component
export const DialogsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [dialogRegistry, setDialogRegistry] = useState<DialogRegistry>({})
    const [pauseDialogs, setPauseDialogs] = useState<boolean>(false)

    const registerDialog = useCallback((key: DialogKeys, dialog: ReactNode, force?: boolean) => {
        const shouldForce = force ?? FORCED_BY_DEFAULT_DIALOGS.includes(key)
        setDialogRegistry((prev) => ({
            ...prev,
            [key]: { dialog, timestamp: shouldForce ? -Date.now() : Date.now() }
        }))
    }, [])

    const unregisterDialog = useCallback((key: DialogKeys, callback?: Function) => {
        setDialogRegistry((prev) => {
            const { [key]: _, ...rest } = prev
            return rest
        })
        callback && callback()
    }, [])

    const dialogs = Object.values(dialogRegistry)
        .sort((a, b) => a.timestamp - b.timestamp)
        .map(entry => entry.dialog)

    const context = {
        dialogs,
        dialogRegistry,
        registerDialog,
        unregisterDialog,
        pauseDialogs,
        setPauseDialogs,
    }

    return <DialogsContext.Provider value={context}>{children}</DialogsContext.Provider>
}

// Hook to use the context
export const useDialogs = (): DialogsContextType => {
    const context = useContext(DialogsContext)
    if (!context) {
        throw new Error('useDialogs must be used within a DialogsProvider')
    }
    return context
}
